import React, { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

// =========================================================================
// <SiteMirror /> — Serves the exact-replica static HTML pages delivered in
// Fundle_Final_Website.zip. Each HTML page lives at
//   /public/site-mirror/<path>/index.html
// and references /assets/*.css + /assets/*.js at absolute paths.
//
// This component:
//   1. Loads the 5 global stylesheets (style, retail-visual, complete-site,
//      site-media, final-polish) once, idempotently.
//   2. Fetches the exact HTML for the current path.
//   3. Extracts <title>, meta description, canonical, JSON-LD and <body>.
//   4. Updates document.title / meta, then injects the body HTML.
//   5. Rebinds any inline <script> tags in the body (and loads the
//      remaining shared JS: app.js, brain-interface.js, complete-site.js,
//      site-media.js) so Brain dialogs, filters, carousels work.
//   6. Intercepts internal <a href="/..."> clicks and routes them through
//      React Router — the site stays SPA-fast, admin + API keep working.
//
// NOTE: public/index.html already carries <div id="root">. The mirror
// renders into <div id="site-mirror-root">, which lives inside <main>.
// =========================================================================

const SHARED_CSS = [
  '/assets/style.css',
  '/assets/retail-visual.css',
  '/assets/complete-site.css',
  '/assets/site-media.css',
  '/assets/final-polish.css',
];

const SHARED_JS = [
  '/assets/app.js',
  '/assets/brain-interface.js',
  '/assets/complete-site.js',
  '/assets/site-media.js',
];

const loadedCss = new Set();
const loadedJs = new Set();

const ensureCss = (href) => {
  if (loadedCss.has(href)) return;
  const link = document.createElement('link');
  link.rel = 'stylesheet';
  link.href = href;
  link.setAttribute('data-mirror-css', '1');
  document.head.appendChild(link);
  loadedCss.add(href);
};

const ensureJs = (src) =>
  new Promise((resolve) => {
    if (loadedJs.has(src)) return resolve();
    const s = document.createElement('script');
    s.src = src;
    s.defer = true;
    s.setAttribute('data-mirror-js', '1');
    s.onload = () => resolve();
    s.onerror = () => resolve(); // best-effort
    document.body.appendChild(s);
    loadedJs.add(src);
  });

// Rebind inline <script> tags inside the injected HTML — the browser does
// not execute them when inserted via innerHTML. We walk the container,
// clone each <script>, and re-attach it so the inline code runs.
const executeInlineScripts = (container) => {
  const scripts = container.querySelectorAll('script');
  scripts.forEach((old) => {
    const neu = document.createElement('script');
    for (const { name, value } of old.attributes) {
      neu.setAttribute(name, value);
    }
    neu.text = old.text;
    old.parentNode.replaceChild(neu, old);
  });
};

const setMeta = (name, value) => {
  if (!value) return;
  let el = document.head.querySelector(`meta[name="${name}"]`);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute('name', name);
    document.head.appendChild(el);
  }
  el.setAttribute('content', value);
};
const setLink = (rel, href) => {
  if (!href) return;
  let el = document.head.querySelector(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement('link');
    el.setAttribute('rel', rel);
    document.head.appendChild(el);
  }
  el.setAttribute('href', href);
};

const mirrorUrlFor = (pathname) => {
  const p = pathname === '/' ? '' : pathname.replace(/\/$/, '');
  return `/site-mirror${p}/index.html`;
};

const SiteMirror = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const containerRef = useRef(null);
  const [status, setStatus] = useState('loading'); // loading | ready | error

  useEffect(() => {
    // Load CSS synchronously
    SHARED_CSS.forEach(ensureCss);
    // Load JS once (async). Won't block render.
    SHARED_JS.forEach((s) => ensureJs(s));
  }, []);

  useEffect(() => {
    const abort = new AbortController();
    setStatus('loading');

    (async () => {
      try {
        const url = mirrorUrlFor(location.pathname);
        const res = await fetch(url, { signal: abort.signal });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const html = await res.text();

        // Parse once in a disposable document so we can lift head + body.
        const doc = new DOMParser().parseFromString(html, 'text/html');

        // ---- Head rewrites ----
        const title = doc.querySelector('title')?.textContent;
        if (title) document.title = title;
        setMeta(
          'description',
          doc.querySelector('meta[name="description"]')?.getAttribute('content'),
        );
        setLink(
          'canonical',
          doc.querySelector('link[rel="canonical"]')?.getAttribute('href'),
        );

        // ---- JSON-LD structured data ----
        // Remove BOTH our own prior mirror blocks AND any build-time prerender
        // JSON-LD (data-seo-ld="prerender") so hydration never duplicates schema.
        document.head
          .querySelectorAll('script[type="application/ld+json"][data-mirror-jsonld], script[data-seo-ld]')
          .forEach((s) => s.remove());
        doc
          .querySelectorAll('script[type="application/ld+json"]')
          .forEach((s) => {
            const neu = document.createElement('script');
            neu.type = 'application/ld+json';
            neu.text = s.text;
            neu.setAttribute('data-mirror-jsonld', '1');
            document.head.appendChild(neu);
          });

        // ---- Inject body ----
        if (containerRef.current) {
          containerRef.current.innerHTML = doc.body.innerHTML;
          executeInlineScripts(containerRef.current);
        }
        setStatus('ready');

        // Scroll to top on route change
        window.scrollTo(0, 0);
      } catch (err) {
        if (err.name !== 'AbortError') {
          console.error('[SiteMirror] load failed', err);
          setStatus('error');
        }
      }
    })();

    return () => abort.abort();
  }, [location.pathname]);

  // Intercept internal link clicks so <a href="/foo"> stays SPA and
  // continues through React Router (keeps admin / api / legacy routes intact).
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const onClick = (e) => {
      let a = e.target;
      while (a && a.tagName !== 'A') a = a.parentElement;
      if (!a) return;
      const href = a.getAttribute('href');
      if (!href) return;
      // Skip external, hash, mailto, tel, download, target=_blank
      if (/^(https?:)?\/\//i.test(href)) return;
      if (href.startsWith('mailto:') || href.startsWith('tel:')) return;
      if (href.startsWith('#')) return;
      if (a.target === '_blank') return;
      if (a.hasAttribute('download')) return;
      e.preventDefault();
      navigate(href);
    };
    el.addEventListener('click', onClick);
    return () => el.removeEventListener('click', onClick);
  }, [navigate, status]);

  return (
    <div
      id="site-mirror-root"
      ref={containerRef}
      data-testid="site-mirror"
      data-mirror-status={status}
      style={status === 'loading' ? { minHeight: '60vh' } : undefined}
    />
  );
};

export default SiteMirror;
